import { Box, Button, HStack, Icon, Radio, Stack, useToast } from "@chakra-ui/react"
import { faPaperPlane } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { yupResolver } from "@hookform/resolvers/yup"
import { useEffect, useMemo } from "react"
import { FormProvider, useForm } from "react-hook-form"
import * as yup from "yup"
import { DateFormControl, InputFormControl, RadioGroupFormControl, SelectFormControl } from "../../components"
import { Export } from "../../components/icons/Export"
import { useTranslationsContext } from "../../context/TranslationsContext"
import { ExportDateTypes, ExportFileFormats, type ExportOption, type ExportRequest } from "../../dtos"
import type { ExportGroup, ExportType, Product } from "../../enums"
import { useGetExportOptions, useGetExportUrl, useSendExportEmail } from "../../hooks"

const schema = (types?: ExportOption[]) => yup.object().shape({
    email: yup.string()
        .when('exportType', {
            is: (exportType: number) => types?.find(x => x.type == exportType)?.isEmailSupported,
            then: () => yup.string().nullable().required('Zadejte email'),
            otherwise: () => yup.string().nullable()
        }),
})

export interface ExportFormProps {
    product?: Product
    group?: ExportGroup
    allowEmail?: boolean
}

export const ExportForm = ({ product, group, allowEmail = true }: ExportFormProps) => {
    const { translate } = useTranslationsContext()
    const { data: exportOptions, isLoading: isLoadingStatisticTypes } = useGetExportOptions(product, group)
    const toast = useToast()
    const methods = useForm<ExportRequest>({
        defaultValues: {
            product: product,
            dateFrom: new Date(new Date().getFullYear(), new Date().getMonth() - 1),
            dateTo: new Date(),
            exportType: exportOptions?.at(0)?.type,
            dateType: ExportDateTypes.ContractedDate,
            fileFormat: ExportFileFormats.Pdf,
        },
        resolver: yupResolver(schema(exportOptions)) as any,
    })
    const { control, watch, trigger, getValues, setValue } = methods

    const exportType = watch('exportType')
    const exportOption = exportOptions?.find(x => x.type === exportType)

    const values = watch()
    const url = useGetExportUrl(values)
    const { mutateAsync: sendEmail, isLoading: isSending } = useSendExportEmail(values)

    const email = watch('email')
    const isEmailSupported = exportOption?.isEmailSupported
    const isEmailed = isEmailSupported && email
    const showFileType = (exportOption?.fileFormats.length ?? 0) > 0
    const showDateRange = exportOption?.isDateRangeSupported
    const showDateType = exportOption?.isDateRangeSupported && exportOption.dateTypes.length > 1

    // Set exportType to first item if not set
    useEffect(() => {
        if (!exportType) {
            setValue('exportType', exportOptions?.at(0)?.type ?? 0 as ExportType)
        }
    }, [exportType, exportOptions, setValue])

    const fileFormat = watch('fileFormat')
    const fileFormats = useMemo(() => exportOption?.fileFormats ?? [], [exportOption])
    // Set fileFormat to first item if not in available formats
    useEffect(() => {
        if (fileFormat && !fileFormats.includes(fileFormat)) {
            setValue('fileFormat', fileFormats[0] ?? ExportFileFormats.Pdf)
        }
    }, [fileFormats, getValues, setValue, fileFormat])

    // Set dateType to first item if not in available date types
    const dateType = watch('dateType')
    const dateTypes = useMemo(() => exportOption?.dateTypes ?? [], [exportOption])
    useEffect(() => {
        if (dateType && !dateTypes.includes(dateType)) {
            setValue('dateType', dateTypes[0] ?? ExportDateTypes.ContractedDate)
        }
    }, [dateTypes, getValues, setValue, dateType])

    return (
        <FormProvider {...methods}>
            <Box
                as="form"
                sx={{
                    display: 'grid',
                    columnGap: { base: 4, xl: 8 },
                    rowGap: { base: 4, xl: 4 },
                    gridTemplateColumns: { base: '1fr', md: 'repeat(4, 1fr)' },
                }}
            >
                <SelectFormControl label={translate('export.type', 'Typ přehledu')} control={control} name="exportType"
                    formControlVariant="fullWidth"
                    valueAsNumber
                    options={exportOptions?.map(x => ({
                        value: x.type?.toString() ?? '',
                        label: x.name ?? '',
                        group: x.group ?? '',
                    }))}
                    isLoading={isLoadingStatisticTypes}
                />

                {showDateRange &&
                    <>
                        <DateFormControl control={control} name='dateFrom' label={translate('dateFrom', "Datum od")} formControlVariant="fullWidth" />

                        <DateFormControl control={control} name='dateTo' label={translate('dateTo', "Datum do")} formControlVariant="fullWidth" />

                        {showDateType &&
                            <RadioGroupFormControl
                                label={translate('export.dateType', "Výběr podle")}
                                name="dateType"
                                control={control}
                                valueAsNumber
                            >
                                <Stack direction="column" gap={3}>
                                    {exportOption?.dateTypes.includes(ExportDateTypes.ContractedDate) && (
                                        <Radio value={ExportDateTypes.ContractedDate.toString()}>{translate('export.dateType.contractedDate', 'Data sjednání smlouvy')}</Radio>
                                    )}
                                    {exportOption?.dateTypes.includes(ExportDateTypes.InsuranceStartDate) && (
                                        <Radio value={ExportDateTypes.InsuranceStartDate.toString()}>{translate('export.dateType.insuranceStartDate', 'Data počátku pojištění')}</Radio>
                                    )}
                                    {exportOption?.dateTypes.includes(ExportDateTypes.ParamsCreatedDate) && (
                                        <Radio value={ExportDateTypes.ParamsCreatedDate.toString()}>{translate('export.dateType.paramsCreatedDate', 'Data vytvoření nabídky')}</Radio>
                                    )}
                                </Stack>
                            </RadioGroupFormControl>
                        }
                    </>
                }

                {showFileType &&
                    <SelectFormControl
                        label={translate('export.fileFormat', "Formát výstupu")}
                        name="fileFormat"
                        control={control}
                        valueAsNumber
                        isDisabled={(exportOption?.fileFormats.length ?? 0) < 2}
                        options={exportOption?.fileFormats.map(format => ({
                            value: format.toString(),
                            label:
                                format === ExportFileFormats.Pdf ? 'PDF' :
                                    format === ExportFileFormats.Xlsx ? 'Excel' :
                                        format === ExportFileFormats.Csv ? 'CSV' :
                                            format === ExportFileFormats.Html ? 'HTML' :
                                                format.toString()
                        }))}
                        formControlVariant="fullWidth"
                    />
                }

                {isEmailSupported && allowEmail &&
                    <InputFormControl name='email' control={control}
                        label={translate('email', "E-mail")}
                        placeholder={translate('export.emailPlaceholder', "Zadejte e-mail, na který export odešleme")}
                        isDisabled={!isEmailSupported}
                        type="email"
                        formControlVariant="fullWidth"
                    />
                }

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        columnGap: 4,
                        rowGap: 4,
                        alignItems: 'end',
                        justifyContent: 'end',
                        gridColumn: { base: '', md: '4 / 5' },
                    }}
                >
                    {!isEmailed &&
                        <Button
                            onClick={() => window.open(url)}
                            colorScheme="primary">
                            <Export />
                            {translate('exportData', 'Exportovat')}
                        </Button>}

                    {isEmailed &&
                        <Button
                            type='button'
                            colorScheme="primary"
                            isLoading={isSending}
                            loadingText={translate('export.sending', 'Odesílám') + '...'}
                            onClick={() => {
                                trigger('email').then((valid) => {
                                    if (!valid) return

                                    sendEmail()
                                        .then(() => {
                                            toast({
                                                title: translate('export.emailSent', 'Email odeslán'),
                                                status: 'success',
                                                duration: 5000,
                                                isClosable: true,
                                            })
                                        })
                                        .catch((e) => {
                                            toast({
                                                title: translate('export.emailNotSent', 'Zprávu se nepodařilo odeslat'),
                                                description: e.response.status === 400 ? e.response.statusText : '',
                                                status: 'error',
                                                duration: 5000,
                                                isClosable: true,
                                            })
                                        })
                                })
                            }}
                        >
                            <HStack gap={2}>
                                <Icon as={FontAwesomeIcon} icon={faPaperPlane} />
                                <span>{translate('send', 'Odeslat')}</span>
                            </HStack>
                        </Button>
                    }
                </Box>
            </Box>
        </FormProvider>
    )
}
